// Global
import React from 'react';

// Local
import { Themes } from 'lib/themes';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { FeatureDecor } from './FeatureDecor/FeatureDecor';
import clsx from 'clsx';
import styles from './FeatureFancy.module.scss';
import { FeatureDetailCard } from './FeatureDetailCard/FeatureDetailCard';

export type FeatureCardAlignment = 'ContentLeft' | 'ContentRight' | 'ContentCenter';
export interface FeatureFancyProps {
  image: React.ReactNode;
  icon: React.ReactNode;
  cta1: React.ReactNode;
  cta2: React.ReactNode;
  headline: React.ReactNode;
  eyebrow: React.ReactNode;
  subheadline: React.ReactNode;
  description: React.ReactNode;
  theme?: Themes;
  contentPosition?: FeatureCardAlignment;
  hideDecor?: boolean;
  hasCta?: boolean;
}

const FeatureCardPositionMap = {
  ContentLeft: 'content-left',
  ContentRight: 'content-right',
  ContentCenter: 'content-center',
} as const;

export const FeatureFancy = (props: FeatureFancyProps) => {
  const contentPosition = props.contentPosition ?? 'ContentLeft';
  const contentPositionClass = FeatureCardPositionMap[contentPosition];

  const hasContent = !!(
    props.icon ||
    props.eyebrow ||
    props.headline ||
    props.subheadline ||
    props.description ||
    props.cta1 ||
    props.cta2
  );

  // If there's no image and no content, the component has nothing to author/show.
  if (!props.image && !hasContent) {
    return null;
  }

  return (
    <BrandAndThemeProvider theme={props.theme}>
      <div id="feature" data-component="authorable/shared/content/feature/featurefancy">
        <div
          className={clsx(
            styles.featureFull,
            styles[contentPositionClass],
            !props.hasCta && styles.noCta
          )}
        >
          {!props.hideDecor && <FeatureDecor />}
          {props.image && (
            <div className={styles.imageContainer}>
              <div className={styles.image}>{props.image}</div>
            </div>
          )}

          {hasContent && (
            <div className={styles.content}>
              <div className={styles.copyContainer}>
                <div className={clsx(styles.copyBlock)}>
                  <FeatureDetailCard
                    {...props}
                    contentAlignment={contentPosition === 'ContentCenter' ? 'center' : 'left'}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </BrandAndThemeProvider>
  );
};
